import React, { useState } from "react";
import { X, ZoomIn, ZoomOut, RotateCw } from "lucide-react";

/**
 * ImagePanel, renders the uploaded image with zoom + clear controls.
 * Pure browser display; the URL is an object URL produced from File.
 *
 * For PDFs we show a placeholder + a "open in new tab" link so the user
 * can flip pages in their browser's PDF viewer alongside the panel.
 */
export default function ImagePanel({ url, fileName, mime, onClear }) {
  const [zoom, setZoom] = useState(1);

  const isPdf = mime === "application/pdf";
  const inc = () => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)));
  const dec = () => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)));
  const reset = () => setZoom(1);

  return (
    <div className="relative h-full flex flex-col rounded-xl overflow-hidden"
      style={{ border: "1px solid #e5e7eb", background: "#ffffff" }}
      data-testid="sbs-image-panel">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-2 border-b text-xs"
        style={{ borderColor: "#e5e7eb", background: "#f3f4f6", color: "#6b7280" }}>
        <span className="truncate max-w-[60%]" title={fileName} data-testid="sbs-image-name">
          {fileName || "Document"}
        </span>
        <button onClick={onClear} className="inline-flex items-center gap-1 hover:underline" data-testid="sbs-clear">
          <X className="w-3.5 h-3.5" /> Clear
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto" style={{ background: "#f3f4f6" }}>
        {isPdf ? (
          <div className="h-full flex flex-col items-center justify-center p-6 text-center">
            <div className="text-4xl mb-3" aria-hidden>📄</div>
            <div className="text-sm font-medium" style={{ color: "#111928" }}>{fileName}</div>
            <p className="text-xs mt-2 max-w-xs" style={{ color: "#6b7280" }}>
              PDFs can't be inlined for privacy, open the file in another tab and refer to it here.
            </p>
            <a href={url} target="_blank" rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium hover:underline"
              style={{ color: "#1d4ed8" }} data-testid="sbs-pdf-open">
              Open PDF in new tab →
            </a>
          </div>
        ) : (
          <div className="min-h-full flex items-start justify-center p-3">
            <img
              src={url}
              alt={fileName || "Uploaded document"}
              data-testid="sbs-image-img"
              style={{
                maxWidth: "100%",
                transform: `scale(${zoom})`,
                transformOrigin: "top center",
                transition: "transform 150ms ease",
                userSelect: "none",
              }}
              draggable={false}
            />
          </div>
        )}
      </div>

      {/* Zoom controls */}
      {!isPdf && (
        <div className="absolute right-3 bottom-3 flex items-center gap-1 rounded-md shadow-sm"
          style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}
          data-testid="sbs-zoom">
          <button onClick={dec} className="px-2 py-1.5 hover:bg-gray-50" aria-label="Zoom out" data-testid="sbs-zoom-out">
            <ZoomOut className="w-3.5 h-3.5" style={{ color: "#6b7280" }} />
          </button>
          <button onClick={reset} className="px-2 py-1.5 text-[11px] font-medium" style={{ color: "#6b7280" }} data-testid="sbs-zoom-level">
            {Math.round(zoom * 100)}%
          </button>
          <button onClick={inc} className="px-2 py-1.5 hover:bg-gray-50" aria-label="Zoom in" data-testid="sbs-zoom-in">
            <ZoomIn className="w-3.5 h-3.5" style={{ color: "#6b7280" }} />
          </button>
          <button onClick={reset} className="px-2 py-1.5 hover:bg-gray-50" aria-label="Reset zoom" data-testid="sbs-zoom-reset">
            <RotateCw className="w-3.5 h-3.5" style={{ color: "#6b7280" }} />
          </button>
        </div>
      )}
    </div>
  );
}
